import type {MoonDomeSample} from './detailView';

/**
 * Where to look, as arithmetic.
 *
 * The arc says how high the moon gets and when; the dome says which way to
 * face. Azimuth runs along the bottom, unwrapped so a moon that never sets
 * sweeps past north rather than tearing the path there, and altitude runs up
 * the side. The samples are `MoonDome.samples`, one for one with the arc's.
 *
 * Nothing here needs a locale and nothing here is a colour. The element tree,
 * the tints, the glyph on the scrubbed point and the label formatting stay in
 * each app — the altitude grid hands back degrees as numbers precisely so the
 * apps print them through their own `intl`, never through a template.
 */

/** Room under the horizon for the compass labels. */
const BOTTOM = 26;
const TOP_PADDING = 12;

/** The altitude grid: every 15°, never fewer than two lines above the horizon. */
const GRID_STEP_DEG = 15;
const GRID_MIN_TOP_DEG = 30;
/** Air above the highest point before the next grid line is chosen. */
const GRID_HEADROOM_DEG = 6;

/**
 * How far below the horizon the path is still drawn, so a pass that starts
 * 30 minutes out does not begin in mid-air: everything under this is dropped,
 * and what remains is clamped to the horizon line.
 */
const PATH_BELOW_DEG = -1;

export type MoonDomeGeometryParams = {
  samples: MoonDomeSample[];
  /** `MoonDome.azimuthRange`. */
  azimuthRange: {from: number; to: number};
  /** `MoonDome.compassTicks`. */
  compassTicks: {azimuth: number; label: string}[];
  /** Measured by the caller. Zero until a layout pass has happened. */
  width: number;
  height: number;
  bottom?: number;
  topPadding?: number;
};

export type MoonDomeGeometry = {
  /** The stroked path across the sky, the below-horizon tail clamped to the line. */
  path: string;
  /** y of the horizon. */
  horizon: number;
  /** The altitude grid, horizon excluded. `altitude` in degrees, for the app to format. */
  grid: {y: number; altitude: number}[];
  /** The compass points along the horizon. */
  ticks: {x: number; label: string}[];
  /** Every sample's place, one for one with `samples`; below the horizon sits on it. */
  points: {x: number; y: number}[];
};

export const skyDomeGeometry = ({
  samples,
  azimuthRange,
  compassTicks,
  width,
  height,
  bottom = BOTTOM,
  topPadding = TOP_PADDING,
}: MoonDomeGeometryParams): MoonDomeGeometry | null => {
  if (width <= 0 || height <= 0 || samples.length < 2) {
    return null;
  }
  const span = azimuthRange.to - azimuthRange.from;
  if (span <= 0) {
    return null;
  }

  const highest = Math.max(...samples.map((sample) => sample.altitude), 0);
  const top = Math.max(
    GRID_MIN_TOP_DEG,
    Math.ceil((highest + GRID_HEADROOM_DEG) / GRID_STEP_DEG) * GRID_STEP_DEG,
  );
  const horizon = height - bottom;

  const x = (azimuth: number) => ((azimuth - azimuthRange.from) / span) * width;
  const y = (altitude: number) =>
    horizon - (Math.max(altitude, 0) / top) * (horizon - topPadding);

  const points = samples.map((sample) => ({
    x: x(sample.azimuth),
    y: y(sample.altitude),
  }));

  const path = samples
    .map((sample, index) =>
      sample.altitude > PATH_BELOW_DEG
        ? (points[index] as {x: number; y: number})
        : null,
    )
    .reduce((d, point) => {
      if (point === null) {
        // A gap: the next visible point starts a new sub-path rather than
        // being joined across the hours the moon spent below the horizon.
        return d.endsWith('|') ? d : `${d}|`;
      }
      const command = d === '' || d.endsWith('|') ? 'M' : 'L';
      return `${d.replace(/\|$/, '')}${command}${point.x.toFixed(1)},${point.y.toFixed(1)}`;
    }, '')
    .replace(/\|$/, '');

  const grid: {y: number; altitude: number}[] = [];
  for (
    let altitude = GRID_STEP_DEG;
    altitude <= top;
    altitude += GRID_STEP_DEG
  ) {
    grid.push({y: y(altitude), altitude});
  }

  return {
    path,
    horizon,
    grid,
    ticks: compassTicks.map((tick) => ({
      x: x(tick.azimuth),
      label: tick.label,
    })),
    points,
  };
};
